import {
    ADD_FOLDER, ADD_LINK, DELETE_FOLDER, DELETE_LINK,
    FETCH_ALL_DATA_SUCCESS, FETCH_DATA_FAILURE, FETCH_DATA_SUCCESS, FETCH_WITH_ARCHIVE_STATUS,
    FOLDER_ASSIGNEE_SUCCESS, LINK_ASSIGNEE_SUCCESS, OPEN_DETAIL_PANE,
    RENAME_FOLDER_SUCCESS, UPDATE_ARCHIVE_STATUS, UPDATE_LINK_SUCCESS, UPDATE_SHORTCUT_SUCCESS, UPDATE_ITEM_STAR_LEVEL,
    SET_SORT_BY_STATUS,
} from "../constants/folders.constant";
import Cookies from "js-cookie";
import ShortcutItem from "../../components/shortcutItem";

const initialState = {
    folders: {},
    depth: 0,
    error: null,
    selected_folders: [],
    links: {},
    shortcuts: {},
    open_folder: null,
    sortByStatus: false,
    // Track latest fetch request id per depth to ignore stale async responses
    latestFetchRequestIdByDepth: {}
};
const custom_status_order = ["Taking Longer", "Doing", "Doing-in-bg", "Paused", "TODO", "Block-on-asm",
                           "Blocked-on-purchase", "Blocked-on-discussion", "Blocked", "Done"];

const getStatusForSorting = (status, custom_status_order) => {
    if (status.length <= 2) {
        const index = parseInt(status, 10) - 1;
        return custom_status_order[index];
    }
    return status;
};

// Comprehensive sorting function: archived items always at bottom
const sortItems = (items, custom_status_order = null, sortByStatus = false) => {
    return items.sort((a, b) => {
        // Primary: archived status (non-archived first)
        const aArchived = a.is_archived || false;
        const bArchived = b.is_archived || false;
        if (aArchived !== bArchived) {
            return aArchived ? 1 : -1; // non-archived (false) comes first
        }
        
        // Secondary and Tertiary: swap based on preference
        if (sortByStatus && custom_status_order && a.status && b.status) {
            // Secondary: status (for folders, using custom order)
            const statusA = getStatusForSorting(a.status, custom_status_order);
            const statusB = getStatusForSorting(b.status, custom_status_order);
            const statusComparison = custom_status_order.indexOf(statusA) - custom_status_order.indexOf(statusB);
            if (statusComparison !== 0) {
                return statusComparison;
            }
            
            // Tertiary: star_level (higher first)
            const aStarLevel = a.star_level || 0;
            const bStarLevel = b.star_level || 0;
            return bStarLevel - aStarLevel;
        } else {
            // Secondary: star_level (higher first)
            const aStarLevel = a.star_level || 0;
            const bStarLevel = b.star_level || 0;
            if (aStarLevel !== bStarLevel) {
                return bStarLevel - aStarLevel;
            }
            
            // Tertiary: status (for folders, using custom order)
            if (custom_status_order && a.status && b.status) {
                const statusA = getStatusForSorting(a.status, custom_status_order);
                const statusB = getStatusForSorting(b.status, custom_status_order);
                return custom_status_order.indexOf(statusA) - custom_status_order.indexOf(statusB);
            }
        }
        
        return 0;
    });
};

const folderReducer = (state = initialState, action) => {
    switch (action.type) {
        case 'FETCH_ALL_DATA_REQUEST':
            return {
                ...state,
                latestAllDataRequestId: action.payload?.requestId
            };

        case 'FETCH_CHILD_REQUEST': {
            const { depth, requestId } = action.payload || {};
            if (!depth) return state;
            return {
                ...state,
                latestFetchRequestIdByDepth: {
                    ...state.latestFetchRequestIdByDepth,
                    [depth]: requestId
                }
            };
        }

        case FETCH_WITH_ARCHIVE_STATUS:
            const fetchedFolders = [...action.payload.folders.folders];
            const fetchedLinks = [...action.payload.folders.links];
            sortItems(fetchedFolders, custom_status_order, state.sortByStatus);
            sortItems(fetchedLinks, null, state.sortByStatus);
            return {
                ...state,
                folders: {
                    [action.payload.depth]: fetchedFolders
                },
                links: {
                    [action.payload.depth]: fetchedLinks
                },
                shortcuts: {
                    [action.payload.depth]: action.payload.folders.shortcuts
                }
            }

        case FETCH_DATA_SUCCESS:
            if (action.payload.child) {
                // If this is a child fetch, ensure the response is the latest for this depth
                const respRequestId = action.payload.requestId;
                const depthKey = action.payload.depth;
                const latestId = state.latestFetchRequestIdByDepth[depthKey];
                if (latestId && respRequestId && respRequestId !== latestId) {
                    // Stale response - ignore
                    return state;
                }
                const sortedFoldersForChild = [...action.payload.folders.folders];
                const sortedLinksForChild = [...action.payload.folders.links];
                sortItems(sortedFoldersForChild, custom_status_order, state.sortByStatus);
                sortItems(sortedLinksForChild, null, state.sortByStatus);
                
                const folders = {
                    ...state.folders,
                    [action.payload.depth]: sortedFoldersForChild
                }

                const links = {
                    ...state.links,
                    [action.payload.depth]: sortedLinksForChild
                }

                const shortcuts = {
                    ...state.shortcuts,
                    [action.payload.depth]: action.payload.folders.shortcuts
                }

                const filteredFolders = Object.keys(folders).reduce((acc, key) => {
                    const folderDepth = parseInt(key, 10);
                    if (folderDepth <= action.payload.depth) {
                        acc[key] = folders[key];
                    }
                    return acc;
                }, {});

                const filteredLinks = Object.keys(links).reduce((acc, key) => {
                    const folderDepth = parseInt(key, 10);
                    if (folderDepth <= action.payload.depth) {
                        acc[key] = links[key];
                    }
                    return acc;
                }, {});

                const filteredShortcuts = Object.keys(shortcuts).reduce((acc, key) => {
                    const folderDepth = parseInt(key, 10);
                    if (folderDepth <= action.payload.depth) {
                        acc[key] = shortcuts[key];
                    }
                    return acc;
                }, {});

                return {
                    ...state,
                    folders: filteredFolders,
                    links: filteredLinks,
                    shortcuts: filteredShortcuts,
                    selected_folders: [...state.selected_folders.slice(0, action.payload.depth - 2), action.payload.folder_id]
                }
            }
            const sortedFolders = [...action.payload.folders.folders];
            const sortedLinks = [...action.payload.folders.links];
            sortItems(sortedFolders, custom_status_order, state.sortByStatus);
            sortItems(sortedLinks, null, state.sortByStatus);
            return {
                ...state,
                folders: {
                    ...state.folders,
                    [action.payload.depth]: sortedFolders
                },
                links: {
                    ...state.links,
                    [action.payload.depth]: sortedLinks
                },
                shortcuts: {
                    ...state.shortcuts,
                    [action.payload.depth]: action.payload.folders.shortcuts
                }
            };

        case FETCH_DATA_FAILURE:
            return {
                ...state,
                folders: {...state.folders},
                links: {...state.links},
                shortcuts: {...state.shortcuts},
                error: action.payload,
            };

        case FETCH_ALL_DATA_SUCCESS:
            // If a request id was provided, ensure it's the latest full-structure request
            const allData = action.payload.data;
            const respRequestId = action.payload.requestId;
            if (respRequestId && state.latestAllDataRequestId && respRequestId !== state.latestAllDataRequestId) {
                // stale response
                return state;
            }
            // Sort folders and links at each depth
            if (allData.folders) {
                Object.keys(allData.folders).forEach(depth => {
                    sortItems(allData.folders[depth], custom_status_order, state.sortByStatus);
                });
            }
            if (allData.links) {
                Object.keys(allData.links).forEach(depth => {
                    sortItems(allData.links[depth], null, state.sortByStatus);
                });
            }
            return {
                ...state,
                ...allData
            }

        case ADD_FOLDER:
            // Add the new folder and sort the list
            const newFolder = { ...action.payload.data };
            const new_folders_list = [newFolder, ...(state.folders[action.payload.depth] || [])];
            sortItems(new_folders_list, custom_status_order, state.sortByStatus);
            return {
                ...state,
                folders: {
                    ...state.folders,
                    [action.payload.depth]: new_folders_list,
                }
            };

        case ADD_LINK:
            // Add the new link and sort the list
            const newLink = { ...action.payload.data };
            const new_links_list = [newLink, ...(state.links[action.payload.depth] || [])];
            sortItems(new_links_list, null, state.sortByStatus);
            return {
                ...state,
                links: {
                    ...state.links,
                    [action.payload.depth]: new_links_list,
                }
            };

        case RENAME_FOLDER_SUCCESS:
            const folder_data = action.payload.folder;
            const folders = state.folders[action.payload.depth];
            const updatedFolders = folders.map(folder => {
                if (folder.id === folder_data.id) {
                    return {...folder, name: folder_data.name, status: folder_data.status, tags: folder_data.tags,
                        star_level: folder_data.star_level, is_archived: folder_data.is_archived};
                }
                return folder;
            });
            
            // Don't filter here - let the backend and frontend rendering handle filtering
            sortItems(updatedFolders, custom_status_order, state.sortByStatus);

            return {
                ...state,
                folders: {
                    ...state.folders,
                    [action.payload.depth]: updatedFolders,
                }
            };

        case FOLDER_ASSIGNEE_SUCCESS:
            const folder_details = action.payload.folder;
            const updated_folders = state.folders[action.payload.depth].map(folder => {
                if (folder.id === folder_details.id) {
                    return {...folder, employees: folder_details.employees};
                }
                return folder;
            });

            return {
                ...state,
                folders: {
                    ...state.folders,
                    [action.payload.depth]: updated_folders,
                }
            };

        case UPDATE_LINK_SUCCESS:
            const link_data = action.payload.link;
            const links = state.links[action.payload.depth];
            const updatedLinks = links.map(link => {
                if (link.id === link_data.id) {
                    return {...link, link: link_data.link, name: link_data.name, tags: link_data.tags,
                        star_level: link_data.star_level, is_archived: link_data.is_archived};
                }
                return link;
            });
            sortItems(updatedLinks, null, state.sortByStatus); // No status sorting for links

            return {
                ...state,
                links: {
                    ...state.links,
                    [action.payload.depth]: updatedLinks,
                }
            };

        case UPDATE_SHORTCUT_SUCCESS:
            const shortcut_data = action.payload.shortcut;
            const shortcuts = state.shortcuts[action.payload.depth];
            const updatedShortcuts = shortcuts.map(shortcut => {
                if (shortcut.id === shortcut_data.id) {
                    return {...shortcut, name: shortcut_data.name, tags: shortcut_data.tags,
                        star_level: shortcut_data.star_level, is_archived: shortcut_data.is_archived};
                }
                return shortcut;
            });
            sortItems(updatedShortcuts, null, state.sortByStatus); // No status sorting for shortcuts

            return {
                ...state,
                shortcuts: {
                    ...state.shortcuts,
                    [action.payload.depth]: updatedShortcuts,
                }
            };

        case LINK_ASSIGNEE_SUCCESS:
            const link_details = action.payload.link;
            const updated_links = state.links[action.payload.depth].map(link => {
                if (link.id === link_details.id) {
                    return {...link, assignee: link_details.assignee};
                }
                return link;
            });

            return {
                ...state,
                links: {
                    ...state.links,
                    [action.payload.depth]: updated_links,
                }
            };

        case DELETE_LINK:
            const link_id = action.payload.link_id;
            const depth = action.payload.depth;
            const links_data = state.links[depth];
            const filteredLinks = links_data.filter(link => {
                return link.id !== link_id;
            });

            return {
                ...state,
                links: {
                    ...state.links,
                    [action.payload.depth]: filteredLinks,
                }
            };

        case UPDATE_ARCHIVE_STATUS:
            const status = action.payload.status;
            return {
                ...state,
                archive_status: status
            }

        case DELETE_FOLDER:
            const folders_data = {
                ...state.folders,
            }

            const linkData = {
                ...state.links,
            }

            const filteredFolders = Object.keys(folders_data).reduce((acc, key) => {
                const folderDepth = parseInt(key, 10);
                if (folderDepth < action.payload.depth) {
                    acc[key] = folders_data[key];
                } else if (folderDepth === action.payload.depth) {
                    acc[key] = folders_data[key].filter(folder => folder.id !== action.payload.folder_id);
                }
                return acc;
            }, {});

            const filteredLinksData = Object.keys(linkData).reduce((acc, key) => {
                const folderDepth = parseInt(key, 10);
                if (folderDepth <= action.payload.depth) {
                    acc[key] = linkData[key];
                }
                return acc;
            }, {});

            return {
                ...state,
                folders: filteredFolders,
                links: filteredLinksData,
                selected_folders: [...state.selected_folders.slice(0, action.payload.depth - 1)]
            }

        case OPEN_DETAIL_PANE:
            const folderId = parseInt(action.folder_id);
            return {
                ...state,
                open_folder: isNaN(folderId) ? action.folder_id : folderId
            }

        case UPDATE_ITEM_STAR_LEVEL:
            // Update star_level WITHOUT sorting - order stays the same until fresh data load
            const { item, depth: itemDepth, itemType } = action.payload;
            
            if (itemType === 'folder' || item.item_type === 'folder') {
                const foldersAtDepth = state.folders[itemDepth];
                if (!foldersAtDepth) return state;
                
                const updatedFoldersNoSort = foldersAtDepth.map(folder => 
                    folder.id === item.id 
                        ? { ...folder, star_level: item.star_level }
                        : folder
                );
                
                return {
                    ...state,
                    folders: {
                        ...state.folders,
                        [itemDepth]: updatedFoldersNoSort
                    }
                };
            } else if (itemType === 'link' || item.item_type === 'link') {
                const linksAtDepth = state.links[itemDepth];
                if (!linksAtDepth) return state;
                
                const updatedLinksNoSort = linksAtDepth.map(link => 
                    link.id === item.id 
                        ? { ...link, star_level: item.star_level }
                        : link
                );
                
                return {
                    ...state,
                    links: {
                        ...state.links,
                        [itemDepth]: updatedLinksNoSort
                    }
                };
            }
            
            return state;

        case SET_SORT_BY_STATUS: {
            console.log('SET_SORT_BY_STATUS reducer called with:', action.payload);
            console.log('Previous sortByStatus:', state.sortByStatus);
            
            // Re-sort all existing data with the new sort preference
            const newSortByStatus = action.payload;
            
            // state.folders and state.links are objects keyed by depth
            // We need to iterate through each depth and re-sort the arrays
            const resortedFolders = {};
            Object.keys(state.folders).forEach(depth => {
                const folderArray = [...state.folders[depth]];
                sortItems(folderArray, custom_status_order, newSortByStatus);
                resortedFolders[depth] = folderArray;
            });
            
            const resortedLinks = {};
            Object.keys(state.links).forEach(depth => {
                const linkArray = [...state.links[depth]];
                sortItems(linkArray, null, newSortByStatus);
                resortedLinks[depth] = linkArray;
            });
            
            return {
                ...state,
                sortByStatus: newSortByStatus,
                folders: resortedFolders,
                links: resortedLinks
            };
        }

        default:
            return state;
    }
};

export default folderReducer;
